/**
 * Types for crew-related data structures
 */

/**
 * Crew role enumeration
 */
export enum CrewRole {
  CAPTAIN = "Captain",
  PILOT = "Pilot",
  ASTROGATOR = "Astrogator",
  ENGINEER = "Engineer",
  MAINTENANCE = "Maintenance",
  GUNNER = "Gunner",
  STEWARD = "Steward",
  ADMINISTRATOR = "Administrator",
  SENSOR_OPERATOR = "Sensor Operator",
  MEDIC = "Medic",
  OFFICER = "Officer",
}

/**
 * Crew type enumeration
 */
export type CrewType =
  | "bridge"
  | "engineering"
  | "gunnery"
  | "medical"
  | "service";

/**
 * Interface for a crew member
 */
export interface CrewMember {
  role: CrewRole;
  skillLevel: number;
  isOfficer: boolean;
  additionalRoles: CrewRole[];
}

/**
 * Interface for a crew member with type
 */
export interface Crew {
  type: CrewType;
  role: CrewRole;
  skillLevel: number;
  isOfficer: boolean;
  additionalRoles: CrewRole[];
}

export interface CrewSelection extends Crew {
  // No additional properties needed as they're all in Crew
}

/**
 * Interface for crew requirements
 */
export interface CrewRequirements {
  captain: number;
  pilots: number;
  astrogators: number;
  engineers: number;
  maintenance: number;
  gunners: number;
  stewards: number;
  administrators: number;
  sensorOperators: number;
  medics: number;
  officers: number;
}

/**
 * Interface for crew composition
 */
export interface CrewComposition {
  pilot: number;
  engineer: number;
  gunner: number;
  medic: number;
  steward: number;
}

/**
 * Interface for crew validation
 */
export interface CrewValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Interface for crew salary
 */
export interface CrewSalary {
  baseSalary: number;
  role: CrewRole;
  description: string;
}

/**
 * Interface for crew type definition
 */
export interface CrewTypeDefinition {
  readonly name: string;
  readonly description: string;
  readonly required: (
    shipSize: number,
    hasJumpDrive: boolean,
    smallCraftCount: number,
    weaponCounts: Record<string, number>,
    passengerCounts: Record<string, number>,
  ) => number;
  readonly baseRequired: (shipSize: number) => number;
  readonly jumpDriveRequired?: number;
  readonly maneuverDriveRequired?: number;
  readonly reactionDriveRequired?: number;
  readonly smallCraftRequired?: number;
  readonly weaponsRequired?: number;
  readonly passengersRequired?: number;
  readonly skills?: string;
  readonly salary?: number;
}

/**
 * Crew mode for requirements calculation
 */
export type CrewMode = "Commercial" | "Military";

/**
 * User-overridden crew numbers for each role
 */
export type CrewOverrides = {
  [role in CrewRole]?: number;
} & {
  "Ship's Troops/Marines"?: number;
};

// Constants
export const CREW_SALARIES: Record<CrewRole, CrewSalary> = {
  [CrewRole.CAPTAIN]: {
    baseSalary: 10000,
    role: CrewRole.CAPTAIN,
    description: "Usually the leading officer",
  },
  [CrewRole.PILOT]: {
    baseSalary: 6000,
    role: CrewRole.PILOT,
    description: "1 plus 1 for each small craft",
  },
  [CrewRole.ASTROGATOR]: {
    baseSalary: 5000,
    role: CrewRole.ASTROGATOR,
    description: "1 if jump drive installed",
  },
  [CrewRole.ENGINEER]: {
    baseSalary: 4000,
    role: CrewRole.ENGINEER,
    description: "1 per 35 tons of drives and power plant",
  },
  [CrewRole.MAINTENANCE]: {
    baseSalary: 1000,
    role: CrewRole.MAINTENANCE,
    description: "1 per 1,000 tons of ship",
  },
  [CrewRole.GUNNER]: {
    baseSalary: 2000,
    role: CrewRole.GUNNER,
    description: "1 per turret, barbette and screen",
  },
  [CrewRole.STEWARD]: {
    baseSalary: 2000,
    role: CrewRole.STEWARD,
    description: "1 per 10 High or 100 Middle passengers",
  },
  [CrewRole.ADMINISTRATOR]: {
    baseSalary: 1500,
    role: CrewRole.ADMINISTRATOR,
    description: "1 per 2,000 tons of ship",
  },
  [CrewRole.SENSOR_OPERATOR]: {
    baseSalary: 4000,
    role: CrewRole.SENSOR_OPERATOR,
    description: "1 per 7,500 tons of ship",
  },
  [CrewRole.MEDIC]: {
    baseSalary: 4000,
    role: CrewRole.MEDIC,
    description: "1 per 120 crew and passengers",
  },
  [CrewRole.OFFICER]: {
    baseSalary: 5000,
    role: CrewRole.OFFICER,
    description: "1 per full 20 crew",
  },
};

export const CREW_REDUCTION_MULTIPLIERS = {
  SMALL: 1, // No reduction for ships under 5,000 tons
  MEDIUM: 0.75, // 5,001-19,999 tons
  LARGE: 0.67, // 20,000-49,999 tons
  HUGE: 0.5, // 50,000-99,999 tons
  MASSIVE: 0.33, // 100,000+ tons
} as const;

export const CREW_REDUCTION_THRESHOLDS = {
  MEDIUM: 5001,
  LARGE: 20000,
  HUGE: 50000,
  MASSIVE: 100000,
} as const;

export const CREW_TYPES: Record<CrewRole, CrewTypeDefinition> = {
  [CrewRole.CAPTAIN]: {
    name: "Captain",
    description: "Required for ship command and overall operation.",
    skills: "—",
    salary: 10000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => 1,
    baseRequired: () => 1,
  },
  [CrewRole.PILOT]: {
    name: "Pilot",
    description:
      "Required for ship operation. One pilot per ship, plus one per small craft.",
    skills: "Pilot",
    salary: 6000,
    required: (shipSize, hasJumpDrive, smallCraftCount) => 1 + smallCraftCount,
    baseRequired: () => 1,
  },
  [CrewRole.ASTROGATOR]: {
    name: "Astrogator",
    description: "Required for jump drive operation.",
    skills: "Astrogation",
    salary: 5000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      return hasJumpDrive ? 1 : 0;
    },
    baseRequired: (shipSize) => 0,
    jumpDriveRequired: 1,
  },
  [CrewRole.ENGINEER]: {
    name: "Engineer",
    description: "Required for drive and power plant operation.",
    skills: "Engineer",
    salary: 4000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      return Math.ceil(shipSize / 35);
    },
    baseRequired: (shipSize) => Math.ceil(shipSize / 35),
  },
  [CrewRole.MAINTENANCE]: {
    name: "Maintenance",
    description: "Required for ship maintenance.",
    skills: "Mechanic",
    salary: 1000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      return Math.ceil(shipSize / 1000);
    },
    baseRequired: (shipSize) => Math.ceil(shipSize / 1000),
  },
  [CrewRole.GUNNER]: {
    name: "Gunner",
    description: "Required for weapon operation.",
    skills: "Gunner",
    salary: 2000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      const totalWeapons = Object.values(weaponCounts || {}).reduce(
        (a, b) => a + b,
        0,
      );
      return totalWeapons;
    },
    baseRequired: (shipSize) => 0,
    weaponsRequired: 1,
  },
  [CrewRole.STEWARD]: {
    name: "Steward",
    description: "Required for passenger service.",
    skills: "Steward",
    salary: 2000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      const high = passengerCounts?.["high"] || 0;
      const middle = passengerCounts?.["middle"] || 0;
      const stewardsForHigh = Math.ceil(high / 10); // Round up
      const stewardsForMiddle = Math.ceil(middle / 100); // Round up
      return stewardsForHigh + stewardsForMiddle;
    },
    baseRequired: (shipSize) => 0,
    passengersRequired: 0, // Remove fractional calculation
  },
  [CrewRole.ADMINISTRATOR]: {
    name: "Administrator",
    description: "Required for ship administration.",
    skills: "Admin",
    salary: 1500,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      return Math.ceil(shipSize / 2000);
    },
    baseRequired: (shipSize) => Math.ceil(shipSize / 2000),
  },
  [CrewRole.SENSOR_OPERATOR]: {
    name: "Sensor Operator",
    description: "Required for sensor operation.",
    skills: "Electronics (sensors)",
    salary: 4000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      return Math.ceil(shipSize / 7500);
    },
    baseRequired: (shipSize) => Math.ceil(shipSize / 7500),
  },
  [CrewRole.MEDIC]: {
    name: "Medic",
    description: "Required for medical care.",
    skills: "Medic",
    salary: 4000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      const totalCrew = Object.values(weaponCounts || {}).reduce(
        (a, b) => a + b,
        0,
      );
      const totalPassengers = Object.values(passengerCounts || {}).reduce(
        (a, b) => a + b,
        0,
      );
      return Math.ceil((totalCrew + totalPassengers) / 120); // Round up for safety - 1 medic for 0-120, 2 for 121-240, etc.
    },
    baseRequired: (shipSize) => 0,
    passengersRequired: 0, // Remove fractional calculation
  },
  [CrewRole.OFFICER]: {
    name: "Officer",
    description: "Required for ship command.",
    skills: "Leadership or Persuade",
    salary: 5000,
    required: (
      shipSize,
      hasJumpDrive,
      smallCraftCount,
      weaponCounts,
      passengerCounts,
    ) => {
      const totalCrew = Object.values(weaponCounts || {}).reduce(
        (a, b) => a + b,
        0,
      );
      return Math.floor(totalCrew / 20); // "1 per full 20 crew" means round down
    },
    baseRequired: (shipSize) => 0,
  },
};

/**
 * Calculate crew salary based on base salary and skill level
 * @param baseSalary - Base salary for the role
 * @param skillLevel - Skill level (1-5)
 * @returns Calculated salary
 */
export const calculateCrewSalary = (baseSalary: number, skillLevel: number): number => {
  // Salary increases by 20% per skill level
  return baseSalary * Math.pow(1.2, skillLevel - 1);
};

/**
 * Get total cost for a role based on assignments
 * @param role - Crew role
 * @param assignment - Assignment object with skill levels as keys and counts as values
 * @returns Total cost for the role
 */
export const getRoleTotalCost = (role: string, assignment: Record<number, number>): number => {
  const baseSalary = CREW_SALARIES[role as CrewRole]?.baseSalary || 0;
  let totalCost = 0;
  
  Object.entries(assignment).forEach(([skillLevel, count]) => {
    const level = parseInt(skillLevel);
    const individualCost = calculateCrewSalary(baseSalary, level);
    totalCost += count * individualCost;
  });
  
  return totalCost;
};
